import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdministrativeRegion,
  BdAdministrativeDistrictId,
} from '@bindoc-projects/geographic-domain-lib';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { AdministrativeRegionStore } from './administrative-region.store';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AdministrativeRegionService {
  private readonly _httpClient = inject(HttpClient);
  private readonly store: AdministrativeRegionStore;

  constructor() {
    const envService = inject(BdAppEnvironmentService);
    this.store = new AdministrativeRegionStore(
      envService.load(ApiEndpointKey.BD_DISTRICT_URL),
    );
  }

  public search(query = ''): Observable<AdministrativeRegion[]> {
    const url = this.store.index();
    const params = new HttpParams().set('q', query);

    return this._httpClient.get<AdministrativeRegion[]>(url, {
      params: params,
    });
  }

  public findAll(
    administrativeRegionIds: number[],
  ): Observable<AdministrativeRegion[]> {
    const url = this.store.index();

    return this._httpClient.post<AdministrativeRegion[]>(url, {
      administrativeRegionIds: administrativeRegionIds,
    });
  }

  public findOne(
    administrativeRegionId: number,
  ): Observable<AdministrativeRegion> {
    const url = this.store.detail(administrativeRegionId);

    return this._httpClient.get<AdministrativeRegion>(url);
  }

  public findItsCareServiceAreas(
    administrativeRegionIds: number[],
  ): Observable<{ [administrativeRegionId: number]: number[] }> {
    const url = this.store.careServiceAreas();

    return this._httpClient.post<{
      [administrativeRegionId: number]: number[];
    }>(url, { administrativeRegionIds: administrativeRegionIds });
  }

  public findItsAdministrativeDistricts(
    administrativeRegionIds: number[],
  ): Observable<BdAdministrativeDistrictId[]> {
    const url = this.store.administrativeDistricts();

    return this._httpClient
      .post<number[]>(url, { administrativeRegionIds: administrativeRegionIds })
      .pipe(
        map((administrativeDistrictIds) =>
          administrativeDistrictIds.map((it) =>
            BdAdministrativeDistrictId.createFromNumber(it),
          ),
        ),
      );
  }

  public findItsPostalCodes(
    administrativeRegionIds: number[],
  ): Observable<string[]> {
    const url = this.store.postalCodes();

    return this._httpClient.post<string[]>(url, {
      administrativeRegionIds: administrativeRegionIds,
    });
  }
}
